import {
  Modal,
  Stepper,
  Group,
  Button,
  Text,
  Stack,
  Box,
} from '@mantine/core';
import {
  ChevronLeft,
  ChevronRight,
} from 'react-feather';
import { UploadStep, MapColumnsStep, ReviewStep, useCSVImport } from './import';
import type { ImportStep } from './import';

interface ImportParticipantsModalProps {
  opened: boolean;
  onClose: () => void;
  cohortId: string;
  cohortName: string;
  partnerName?: string;
  onImportComplete: () => void;
  existingParticipantCount?: number;
}

export function ImportParticipantsModal({
  opened,
  onClose,
  cohortId,
  cohortName,
  partnerName,
  onImportComplete,
  existingParticipantCount = 0,
}: ImportParticipantsModalProps) {
  const {
    file,
    csvData,
    csvHeaders,
    activeStep,
    previewRows,
    importing,
    previewing,
    importResult,
    includedCustomColumns,
    isReupload,
    visibleMappings,
    reviewColumns,
    headerSubtitle,
    canProceed,
    mappingSummary,
    previewStats,
    emptyOptionalFieldsCount,
    handleClose,
    handleFileDrop,
    handleNext,
    handleImport,
    handleViewParticipants,
    updateMapping,
    toggleCustomInclusion,
    ignoreColumn,
    getSampleValues,
    getCsvValue,
    getFieldType,
    setActiveStep,
    setFile,
    setCsvHeaders,
    setCsvData,
    setMappings,
  } = useCSVImport({
    cohortId,
    cohortName,
    partnerName,
    existingParticipantCount,
    onClose,
    onImportComplete,
  });

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Box>
          <Text fw={600} size="lg">Add Participants</Text>
          <Text size="sm" c="dimmed">{headerSubtitle}</Text>
        </Box>
      }
      size={1350}
      centered
      styles={{
        header: { paddingBottom: 0 },
        body: { paddingTop: 'var(--mantine-spacing-md)' },
      }}
    >
      <Stack gap="lg">
        {/* Stepper - 3 steps */}
        <Stepper
          active={activeStep}
          size="sm"
          styles={{
            stepLabel: { fontWeight: 500 },
            stepDescription: { fontSize: 'var(--mantine-font-size-xs)' },
          }}
        >
          <Stepper.Step label="Upload" description="Add participant data" />
          <Stepper.Step label="Map Columns" description="Confirm field mapping" />
          <Stepper.Step label="Review" description="Edit & import" />
        </Stepper>

        {/* Step 1: Upload */}
        {activeStep === 0 && (
          <UploadStep
            onFileDrop={handleFileDrop}
            file={file}
            csvDataRowCount={csvData.length}
            existingCount={existingParticipantCount}
            isReupload={isReupload}
          />
        )}

        {/* Step 2: Map Columns */}
        {activeStep === 1 && (
          <MapColumnsStep
            file={file}
            csvDataRowCount={csvData.length}
            csvHeaderCount={csvHeaders.length}
            visibleMappings={visibleMappings}
            includedCustomColumns={includedCustomColumns}
            mappingSummary={mappingSummary}
            onUpdateMapping={updateMapping}
            onToggleCustom={toggleCustomInclusion}
            onIgnoreColumn={ignoreColumn}
            getSampleValues={getSampleValues}
            getFieldType={getFieldType}
            onChangeFile={() => {
              setFile(null);
              setCsvHeaders([]);
              setCsvData([]);
              setMappings([]);
              setActiveStep(0);
            }}
          />
        )}

        {/* Step 3: Review (or Confirmation if import complete) */}
        {activeStep === 2 && (
          <ReviewStep
            previewRows={previewRows}
            previewStats={previewStats}
            reviewColumns={reviewColumns}
            importResult={importResult}
            emptyOptionalFieldsCount={emptyOptionalFieldsCount}
            importing={importing}
            canProceed={canProceed}
            cohortName={cohortName}
            onImport={handleImport}
            onViewParticipants={handleViewParticipants}
            onBack={() => setActiveStep(1)}
            getCsvValue={getCsvValue}
          />
        )}

        {/* Footer for steps 0 and 1 */}
        {activeStep < 2 && (
          <Group justify="space-between">
            {activeStep > 0 ? (
              <Button
                variant="default"
                leftSection={<ChevronLeft size={16} />}
                onClick={() => setActiveStep((prev) => Math.max(prev - 1, 0) as ImportStep)}
              >
                Back
              </Button>
            ) : (
              <Box />
            )}
            <Button
              onClick={handleNext}
              disabled={!canProceed()}
              loading={previewing}
              rightSection={<ChevronRight size={16} />}
            >
              Continue to Review
            </Button>
          </Group>
        )}
      </Stack>
    </Modal>
  );
}
